import { useEffect, useMemo, useRef, useState } from "react";
import type { MessageBodyCacheStatus } from "@/features/inbox/MessageBodyCacheBadge";
import { INBOX_BODY_STATUS_RETRY_MS, INBOX_CACHE_WINDOW_SIZE } from "@/lib/constants/inbox";
import { reconcileVisibleBodyFlags } from "@/lib/mail/bodyCacheReconcile";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";
import {
  MAIL_BODY_WARMED_EVENT,
  type MailBodyWarmedDetail,
} from "@/lib/mail/mailBodyWarmEvents";
import {
  MAIL_BODY_WARMING_EVENT,
  type MailBodyWarmingDetail,
  getWarmingUids,
  resolveMessageBodyCacheStatus,
  subscribeBodyWarmState,
} from "@/lib/mail/mailBodyWarmState";
import {
  primeMessageBodyCacheFromIndexedDB,
  subscribeMessageBodyCache,
} from "@/lib/cache/messageBodyCache";
import { unifiedMessageKey } from "@/lib/unified/unifiedInboxMerge";

export type MultiAccountBodyCacheEntry = {
  accountId: number;
  uid: number;
  folder: string;
  bodyCached?: boolean;
};

function serverFlagKey(accountId: number, folder: string, uid: number): string {
  return `${accountId}\0${folder}\0${uid}`;
}

function mergeFlagMaps(
  prev: Map<string, boolean>,
  next: Map<string, boolean>
): Map<string, boolean> {
  if (next.size === 0) return prev;
  let changed = false;
  const merged = new Map(prev);
  for (const [key, value] of next) {
    if (merged.get(key) !== value) {
      changed = true;
      merged.set(key, value);
    }
  }
  return changed ? merged : prev;
}

/** Pastilles corps — liste multicompte (nuage / 1 coche / 2 coches). */
export function useMultiAccountBodyCacheFlags(
  entries: MultiAccountBodyCacheEntry[]
): Map<string, MessageBodyCacheStatus> {
  const [clientVersion, setClientVersion] = useState(0);
  const [warmVersion, setWarmVersion] = useState(0);
  const [serverFlags, setServerFlags] = useState<Map<string, boolean>>(new Map());
  const entryKey = useMemo(
    () => entries.map((e) => `${e.accountId}:${e.uid}\0${e.folder}`).join("|"),
    [entries]
  );
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const fetchGen = useRef(0);
  const retryGen = useRef(0);
  const accountIds = useMemo(
    () => [...new Set(entries.map((e) => e.accountId))].sort((a, b) => a - b).join(","),
    [entries]
  );

  useEffect(() => {
    return subscribeMessageBodyCache(() => setClientVersion((v) => v + 1));
  }, []);

  useEffect(() => {
    return subscribeBodyWarmState(() => setWarmVersion((v) => v + 1));
  }, []);

  useEffect(() => {
    const onBodiesWarming = (event: Event) => {
      const detail = (event as CustomEvent<MailBodyWarmingDetail>).detail;
      if (!detail) return;
      if (!entriesRef.current.some((e) => e.accountId === detail.accountId)) return;
      setWarmVersion((v) => v + 1);
    };
    const onBodiesWarmed = (event: Event) => {
      const detail = (event as CustomEvent<MailBodyWarmedDetail>).detail;
      if (!detail) return;
      if (!entriesRef.current.some((e) => e.accountId === detail.accountId)) return;

      setServerFlags((prev) => {
        const next = new Map(prev);
        for (const [key, ready] of Object.entries(detail.flags)) {
          const uid = Number(key);
          if (uid > 0 && ready) {
            next.set(serverFlagKey(detail.accountId, detail.folder, uid), true);
          }
        }
        return mergeFlagMaps(prev, next);
      });
      setWarmVersion((v) => v + 1);
    };
    window.addEventListener(MAIL_BODY_WARMING_EVENT, onBodiesWarming);
    window.addEventListener(MAIL_BODY_WARMED_EVENT, onBodiesWarmed);
    return () => {
      window.removeEventListener(MAIL_BODY_WARMING_EVENT, onBodiesWarming);
      window.removeEventListener(MAIL_BODY_WARMED_EVENT, onBodiesWarmed);
    };
  }, [accountIds]);

  useEffect(() => {
    if (entryKey.length === 0) {
      setServerFlags(new Map());
      return;
    }

    const currentEntries = entriesRef.current;
    const byAccountFolder = new Map<string, number[]>();

    for (const entry of currentEntries) {
      if (entry.uid <= 0) continue;
      const groupKey = `${entry.accountId}\0${entry.folder}`;
      const list = byAccountFolder.get(groupKey) ?? [];
      if (list.length < INBOX_CACHE_WINDOW_SIZE) {
        list.push(entry.uid);
      }
      byAccountFolder.set(groupKey, list);
    }

    retryGen.current += 1;
    const localRetryGen = retryGen.current;
    let cancelled = false;

    const loadFlags = async (): Promise<boolean> => {
      const gen = ++fetchGen.current;
      const merged = new Map<string, boolean>();
      let anyOk = false;

      for (const [groupKey, folderUids] of byAccountFolder) {
        const [accountIdStr, folder] = groupKey.split("\0");
        const accountId = Number(accountIdStr);
        const flags = await reconcileVisibleBodyFlags({
          accountId,
          folder,
          uids: folderUids,
          force: true,
        });
        if (gen !== fetchGen.current) return false;
        if (!flags) continue;
        anyOk = true;
        for (const [uid, value] of flags) {
          merged.set(serverFlagKey(accountId, folder, uid), value);
        }
      }

      if (gen !== fetchGen.current) return false;
      if (anyOk) {
        setServerFlags((prev) => mergeFlagMaps(prev, merged));
      }

      return anyOk;
    };

    void (async () => {
      await Promise.all(
        [...byAccountFolder.entries()].map(([groupKey, uids]) => {
          const [accountIdStr, folder] = groupKey.split("\0");
          return primeMessageBodyCacheFromIndexedDB(Number(accountIdStr), folder, uids);
        })
      );
      if (cancelled) return;
      setClientVersion((v) => v + 1);

      const ok = await loadFlags();
      if (cancelled || !ok) return;

      for (const delay of INBOX_BODY_STATUS_RETRY_MS) {
        window.setTimeout(() => {
          if (localRetryGen !== retryGen.current) return;
          void loadFlags();
        }, delay);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entryKey]);

  return useMemo(() => {
    const map = new Map<string, MessageBodyCacheStatus>();

    for (const entry of entries) {
      if (entry.uid <= 0) continue;
      const rowKey = unifiedMessageKey(entry.accountId, entry.uid);
      const flagKey = serverFlagKey(entry.accountId, entry.folder, entry.uid);
      const warmingNow = getWarmingUids(entry.accountId, entry.folder).has(entry.uid);
      const fromList = entry.bodyCached === true;
      map.set(
        rowKey,
        resolveMessageBodyCacheStatus({
          fromList,
          warmingNow,
          clientCached: isMessageBodyCached(entry.accountId, entry.folder, entry.uid),
          serverCached: serverFlags.get(flagKey) ?? false,
        })
      );
    }

    return map;
  }, [clientVersion, entries, entryKey, serverFlags, warmVersion]);
}
